// MessengerPress Phase 2 — admin-only API client (issue #2392).
// Thin axios wrappers around the 6 backend endpoints registered by
// apps/mdl-backend/src/routes/messenger-press.routes.ts. Every call goes
// through the existing axios instance so the bearer token + base URL are
// applied automatically.

import axios from '@/api/axios.config'
import type { PdfStatusKey } from '@messenger/types'

// Single op shape — kept loose on the FE because the BE Zod schema is the
// authoritative validator. Dimensional values are in INCHES (the BE
// converts to points at run time inside the orchestrator).
export type MessengerPressOperation = {
  type: string
  [key: string]: unknown
}

// Preset stored at s3://dignity-pdf-masks/<env>/presets/<productId>__<variant>.json
// and returned by GET /v1/messenger-press/presets.
export type MessengerPressPreset = {
  id: string
  name: string
  productId: string
  isDefault: boolean
  createdAt?: string
  updatedAt?: string
  operations: MessengerPressOperation[]
}

// Source PDF row returned by GET /v1/messenger-press/source-pdfs.
// `productId` is the BASE product UUID (resolved by the BE through the
// matching OrderProduct/M3OrderProduct row); `isM3` indicates which side
// of the dual-universe the source PDF was generated for. The display
// fields (caseName, funeralHomeName, orderShort) are joined server-side
// so the picker can show human-friendly rows without N+1 lookups.
export type MessengerPressSourcePdf = {
  id: string
  productId: string
  productName: string
  isM3: boolean
  orderId: string | null
  orderShort: string | null
  caseName: string | null
  funeralHomeName: string | null
  createdAt: string
  url: string
  thumbnailUrl?: string
}

export type MessengerPressSourcePdfsResponse = {
  pdfs: MessengerPressSourcePdf[]
  page: number
  pageSize: number
  total: number
}

// GeneratedPrintReadyPdf job row returned by every endpoint that touches the
// GeneratedPrintReadyPdf table (POST, GET /:id, GET list). The BE adds a resolved
// `downloadUrl` so the FE never has to know about S3 bucket names.
export type MessengerPressJob = {
  id: string
  sourcePdfId: string | null
  sourcePdfUrl: string | null
  productId: string | null
  operations: MessengerPressOperation[]
  status: PdfStatusKey
  progress: number
  url: string | null
  downloadUrl: string | null
  errorMessage: string | null
  createdBy: string | null
  createdAt: string
  updatedAt: string
  // BE-joined display fields (list endpoint only). Null when the job
  // isn't tied back to an OrderProduct with a reachable case.
  caseName?: string | null
  funeralHomeName?: string | null
  productName?: string | null
}

// POST /v1/messenger-press payload — supports both byProduct and
// byOperations modes from the BE Zod schema.
export type CreateMessengerPressPayload =
  | {
      mode: 'byProduct'
      productId: string
      themeId?: string
      sourcePdfId?: string
      sourcePdfUrl?: string
    }
  | {
      mode: 'byOperations'
      operations: MessengerPressOperation[]
      themeId?: string
      sourcePdfId?: string
      sourcePdfUrl?: string
    }

export const getMessengerPressSourcePdfs = async (params?: {
  q?: string
  page?: number
  pageSize?: number
}) =>
  await axios.get<MessengerPressSourcePdfsResponse>(
    '/messenger-press/source-pdfs',
    { params },
  )

export const getMessengerPressPresets = async (productId?: string) =>
  await axios.get<{ presets: MessengerPressPreset[] }>(
    '/messenger-press/presets',
    productId ? { params: { productId } } : undefined,
  )

export const getMessengerPressPreset = async (
  productId: string,
  variant: string,
) =>
  await axios.get<{ preset: MessengerPressPreset }>(
    `/messenger-press/presets/${productId}/${variant}`,
  )

export const putMessengerPressPreset = async (
  productId: string,
  variant: string,
  preset: MessengerPressPreset,
) =>
  await axios.put<{ preset: MessengerPressPreset }>(
    `/messenger-press/presets/${productId}/${variant}`,
    preset,
  )

export const deleteMessengerPressPreset = async (
  productId: string,
  variant: string,
) =>
  await axios.delete<void>(`/messenger-press/presets/${productId}/${variant}`)

export const promoteMessengerPressPresetToDefault = async (
  productId: string,
  variant: string,
) =>
  await axios.post<{ preset: MessengerPressPreset }>(
    `/messenger-press/presets/${productId}/${variant}/promote-default`,
  )

export const listMessengerPressMasks = async () =>
  await axios.get<{ masks: string[] }>('/messenger-press/masks')

export type MessengerPressJobsResponse = {
  jobs: MessengerPressJob[]
  page: number
  pageSize: number
  total: number
}

export const listMessengerPressJobs = async (params?: {
  page?: number
  pageSize?: number
}) =>
  await axios.get<MessengerPressJobsResponse>('/messenger-press', {
    params,
  })

export const getMessengerPressJob = async (id: string) =>
  await axios.get<MessengerPressJob>(`/messenger-press/${id}`)

export const createMessengerPressJob = async (
  payload: CreateMessengerPressPayload,
) => await axios.post<MessengerPressJob>('/messenger-press', payload)

export type MessengerPressUploadSourceResponse = {
  sourcePdfUrl: string
  key: string
  size: number
  filename: string
}

export const uploadMessengerPressSource = async (file: File) => {
  const form = new FormData()
  form.append('file', file)
  return await axios.post<MessengerPressUploadSourceResponse>(
    '/messenger-press/upload-source',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
}
